import React, { useMemo, useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, Dimensions, PanResponder, ActivityIndicator } from 'react-native';
import { AreaChart, LineChart } from 'react-native-svg-charts';
import Svg, { Defs, LinearGradient, Stop, Circle } from 'react-native-svg';
import * as shape from 'd3-shape';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { supabase } from '../lib/supabase';
import { computePaddedDomain, projectValueToPlotY } from '../lib/chartAxis';

// Chart geometry — single source for the SVG height and contentInset. The
// AreaChart/LineChart map the Y domain into the plot area (height minus the
// top/bottom inset), and the tooltip-dot overlay projects through the SAME
// numbers via projectValueToPlotY so it lands on the line. See chartAxis.ts.
const CHART_HEIGHT = 160;
const CHART_CONTENT_INSET = { top: 20, bottom: 40, left: 0, right: 0 };

interface VariantValuation {
  valuation_date: string;
  avg_price: number;
  min_price: number | null;
  max_price: number | null;
}

type TimePeriod = '1W' | '1M' | '3M' | 'YTD' | 'All';

interface Props {
  variantId: string;
  currentValuation?: number;
  // When provided, render this per-variant strict-Used series directly instead
  // of querying Supabase. The unified listing detail (plan catalog-buy-unification
  // A2) already holds the series from the catalog API (variant.price_history), so
  // it passes it here rather than firing a second client read against a
  // service-role-gated relation. The line/area render only needs {date,
  // avg_price}; min/max are unused, so the caller's narrow shape is enough.
  series?: { date: string; avg_price: number }[];
}

export default function ItemPriceChart({ variantId, currentValuation, series }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          marginTop: theme.spacing.lg,
          marginBottom: theme.spacing.sm,
        },
        heading: {
          marginBottom: theme.spacing.sm,
        },
        stateBox: {
          padding: theme.spacing.xl,
          alignItems: 'center',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.backgroundSurface,
        },
        stateBoxLabel: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.sm,
        },
        emptyText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        emptySubtext: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          letterSpacing: 0,
        },
        changeInfo: {
          marginBottom: theme.spacing.sm,
        },
        changeText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
        },
        timePeriod: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        changePositive: {
          color: theme.colors.trendUp,
        },
        changeNegative: {
          color: theme.colors.trendDown,
        },
        chartContainer: {
          marginBottom: theme.spacing.sm,
          overflow: 'hidden',
        },
        periodSelector: {
          flexDirection: 'row',
          justifyContent: 'space-around',
        },
        periodButton: {
          paddingVertical: theme.spacing.xs,
          paddingHorizontal: theme.spacing.md,
          borderRadius: theme.radius.radiusLg,
          backgroundColor: 'transparent',
        },
        periodButtonActive: {
          backgroundColor: theme.colors.accent,
        },
        periodButtonText: {
          ...theme.typography.xs,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textSecondary,
          letterSpacing: 0,
        },
        periodButtonTextActive: {
          color: theme.colors.backgroundPrimary,
        },
        tooltip: {
          position: 'absolute',
          backgroundColor: theme.colors.backgroundElevated,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.md,
          borderRadius: theme.radius.radiusMd,
          minWidth: 120,
          alignItems: 'center',
          ...theme.shadows.cardShadow,
        },
        tooltipDate: {
          ...theme.typography.xs,
          color: theme.colors.textSecondary,
          letterSpacing: 0,
        },
        tooltipValue: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
          marginTop: theme.spacing.xxs,
        },
      }),
    [theme],
  );
  const [data, setData] = useState<VariantValuation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('1M');
  const [selectedPoint, setSelectedPoint] = useState<{ x: number; y: number; date: Date } | null>(null);

  useEffect(() => {
    if (series != null) {
      // Caller-supplied series (A2): map to the internal shape and skip the
      // read. min/max are absent on the catalog series and unused by the render.
      setData(
        series.map((p) => ({
          valuation_date: p.date,
          avg_price: p.avg_price,
          min_price: null,
          max_price: null,
        })),
      );
      setLoading(false);
      return;
    }
    fetchPriceHistory();
  }, [variantId, series]);

  const fetchPriceHistory = async () => {
    if (!variantId) {
      setLoading(false);
      return;
    }

    try {
      // Read the strict-Used, one-row-per-day display view rather than the raw
      // variant_valuations table. The view filters to condition='Used' and
      // collapses same-day multi-source rows (lowest source_id wins, mirroring
      // pricing_display_headline_used), so the chart's most-recent real point
      // reconciles with the headline above it and no longer whipsaws across
      // conditions. The view isn't in the generated Supabase types, so cast —
      // same pattern as variantOptions.ts.
      const { data: valuations, error } = await (supabase as any)
        .from('pricing_display_history_used')
        .select('valuation_date, avg_price, min_price, max_price')
        .eq('variant_id', variantId)
        .order('valuation_date', { ascending: true });

      if (error) {
        console.error('Error fetching price history:', error);
        setData([]);
      } else {
        setData(valuations || []);
      }
    } catch (err) {
      console.error('Error fetching price history:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.stateBox}>
          <ActivityIndicator size="small" color={theme.colors.accent} />
          <Text style={styles.stateBoxLabel}>Loading price history...</Text>
        </View>
      </View>
    );
  }

  // If no historical data but we have current valuation, show single point message
  if (!data || data.length === 0) {
    if (currentValuation && currentValuation > 0) {
      return (
        <View style={styles.container}>
          <Text style={[commonStyles.sectionLabel, styles.heading]}>Price History</Text>
          <View style={styles.stateBox}>
            <Text style={styles.emptyText}>Tracking started</Text>
            <Text style={styles.emptySubtext}>Price trends will appear after the next market update</Text>
          </View>
        </View>
      );
    }
    return (
      <View style={styles.container}>
        <Text style={[commonStyles.sectionLabel, styles.heading]}>Price History</Text>
        <View style={styles.stateBox}>
          <Text style={styles.emptyText}>No price history yet</Text>
          <Text style={styles.emptySubtext}>Price trends will appear as market data is collected</Text>
        </View>
      </View>
    );
  }

  // Filter data based on selected period
  const filterDataByPeriod = (period: TimePeriod) => {
    const now = new Date();
    now.setHours(23, 59, 59, 999);

    const sorted = [...data].sort((a, b) =>
      new Date(a.valuation_date).getTime() - new Date(b.valuation_date).getTime()
    );

    let startDate: Date;
    switch (period) {
      case '1W':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '1M':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 29);
        startDate.setHours(0, 0, 0, 0);
        break;
      case '3M':
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 89);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'YTD':
        startDate = new Date(now.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'All':
      default:
        return sorted;
    }

    const filtered = sorted.filter(d => new Date(d.valuation_date) >= startDate);

    if (filtered.length === 0) return [];

    const result: VariantValuation[] = [];
    const firstDataPoint = filtered[0];
    const firstDate = new Date(firstDataPoint.valuation_date);

    // Backfill from startDate to first data point
    const daysBeforeFirst = Math.floor((firstDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    if (daysBeforeFirst > 0) {
      for (let i = 0; i < daysBeforeFirst; i++) {
        const fillDate = new Date(startDate.getTime() + i * 24 * 60 * 60 * 1000);
        result.push({
          valuation_date: fillDate.toISOString(),
          avg_price: firstDataPoint.avg_price,
          min_price: firstDataPoint.min_price,
          max_price: firstDataPoint.max_price,
        });
      }
    }

    // Add actual data
    result.push(...filtered);

    // Forward fill to today by holding the last real market value flat. We
    // have no newer market data, so the most-recent point stays equal to the
    // latest view row — which equals the pricing_display_headline_used
    // headline for market_median variants. Since the price-basis ladder
    // (migration 20260612_headline_price_basis) the headline may instead be
    // the trust-gated anchor, in which case the chart deliberately keeps
    // plotting the market series and may differ from the headline — see
    // docs/contracts/pricing-display-contract.md "Reconciliation caveat".
    // Deliberately NOT currentValuation: that prop carries the stored
    // per-item valuation (item.valuation), a different source that made the
    // chart's last point disagree with the headline rendered directly above.
    const lastDataPoint = filtered[filtered.length - 1];
    const lastDate = new Date(lastDataPoint.valuation_date);
    const daysAfterLast = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysAfterLast > 0) {
      for (let i = 1; i <= daysAfterLast; i++) {
        const fillDate = new Date(lastDate.getTime() + i * 24 * 60 * 60 * 1000);
        result.push({
          valuation_date: fillDate.toISOString(),
          avg_price: lastDataPoint.avg_price,
          min_price: lastDataPoint.min_price,
          max_price: lastDataPoint.max_price,
        });
      }
    }

    return result;
  };

  const sortedData = filterDataByPeriod(selectedPeriod);

  if (sortedData.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.stateBox}>
          <Text style={styles.emptyText}>No data for this period</Text>
        </View>
        <View style={styles.periodSelector}>
          {(['1W', '1M', '3M', 'YTD', 'All'] as TimePeriod[]).map((period) => (
            <TouchableOpacity
              key={period}
              style={[
                styles.periodButton,
                selectedPeriod === period && styles.periodButtonActive,
              ]}
              onPress={() => setSelectedPeriod(period)}
            >
              <Text style={[
                styles.periodButtonText,
                selectedPeriod === period && styles.periodButtonTextActive,
              ]}>
                {period}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  }

  // Prepare chart data
  let chartData = sortedData
    .filter(d => d.avg_price > 0)
    .map((d, index) => ({
      x: index,
      y: d.avg_price,
      date: new Date(d.valuation_date),
    }));

  // If only 1 data point, extend it flat to today so we can draw a line rather
  // than fall through to the empty state. currentValuation gates whether to
  // extend (we only do so when there's a live value signal), but the point's
  // value is the data point's own (view) value so the endpoint still matches
  // the headline — not currentValuation, which is a different source.
  if (chartData.length === 1 && currentValuation && currentValuation > 0) {
    const today = new Date();
    chartData.push({
      x: 1,
      y: chartData[0].y,
      date: today,
    });
  }

  if (chartData.length < 2) {
    return (
      <View style={styles.container}>
        <Text style={[commonStyles.sectionLabel, styles.heading]}>Price History</Text>
        <View style={styles.stateBox}>
          <Text style={styles.emptyText}>Not enough data yet</Text>
          <Text style={styles.emptySubtext}>Check back in a few days to see price trends</Text>
        </View>
      </View>
    );
  }

  // Calculate change
  const firstValue = chartData[0]?.y || 0;
  const lastValue = chartData[chartData.length - 1]?.y || 0;
  const changeAmount = lastValue - firstValue;
  const changePercent = firstValue > 0 ? ((changeAmount / firstValue) * 100) : 0;

  // Padded, non-zero-based Y domain shared by the area fill, the line, and the
  // tooltip-dot positioning so a small genuine move stays legible and the dot
  // lands on the line. See src/lib/chartAxis.ts.
  const yDomain = computePaddedDomain(chartData.map(d => d.y));

  // Up = trendUp (green), down = trendDown (orange).
  const chartColor = changeAmount >= 0 ? theme.colors.trendUp : theme.colors.trendDown;
  // Direction-scoped gradient id so an SVG def from a previous render (or
  // a sibling chart instance) doesn't bleed its colour through. Hardcoding
  // `id="gradient"` caused the area shading to stay green even when the
  // line correctly flipped to orange on a falling chart.
  const gradientId = changeAmount >= 0 ? 'gradient-up' : 'gradient-down';
  const screenWidth = Dimensions.get('window').width - 32; // Account for container padding

  const getPeriodLabel = (period: TimePeriod): string => {
    switch (period) {
      case '1W': return 'Past week';
      case '1M': return 'Past month';
      case '3M': return 'Past 3 months';
      case 'YTD': return 'Year to date';
      case 'All': return 'All time';
      default: return '';
    }
  };

  return (
    <View style={styles.container}>
      <Text style={[commonStyles.sectionLabel, styles.heading]}>Price History</Text>

      <View style={styles.changeInfo}>
        <Text style={[
          styles.changeText,
          changeAmount >= 0 ? styles.changePositive : styles.changeNegative,
        ]}>
          {changeAmount >= 0 ? '+' : ''}${Math.abs(changeAmount).toFixed(0)} ({changePercent >= 0 ? '+' : ''}{changePercent.toFixed(1)}%) <Text style={styles.timePeriod}>{getPeriodLabel(selectedPeriod)}</Text>
        </Text>
      </View>

      <View
        style={styles.chartContainer}
        {...PanResponder.create({
          onStartShouldSetPanResponder: () => true,
          onMoveShouldSetPanResponder: () => true,
          onPanResponderGrant: (evt) => {
            const x = evt.nativeEvent.locationX;
            const index = Math.round((x / screenWidth) * (chartData.length - 1));
            const clampedIndex = Math.max(0, Math.min(chartData.length - 1, index));
            if (chartData[clampedIndex]) {
              setSelectedPoint(chartData[clampedIndex]);
            }
          },
          onPanResponderMove: (evt) => {
            const x = evt.nativeEvent.locationX;
            const index = Math.round((x / screenWidth) * (chartData.length - 1));
            const clampedIndex = Math.max(0, Math.min(chartData.length - 1, index));
            if (chartData[clampedIndex]) {
              setSelectedPoint(chartData[clampedIndex]);
            }
          },
          onPanResponderRelease: () => {},
        }).panHandlers}
      >
        <AreaChart
          style={{ height: CHART_HEIGHT }}
          data={chartData.map(d => d.y)}
          contentInset={CHART_CONTENT_INSET}
          curve={shape.curveMonotoneX}
          svg={{ fill: `url(#${gradientId})` }}
          gridMin={yDomain.min}
          gridMax={yDomain.max}
          yMin={yDomain.min}
          yMax={yDomain.max}
        >
          <Defs key={gradientId}>
            <LinearGradient id={gradientId} x1={'0%'} y1={'0%'} x2={'0%'} y2={'100%'}>
              <Stop offset={'0%'} stopColor={chartColor} stopOpacity={0.25} />
              <Stop offset={'100%'} stopColor={chartColor} stopOpacity={0.02} />
            </LinearGradient>
          </Defs>
        </AreaChart>

        <LineChart
          style={{ height: CHART_HEIGHT, position: 'absolute', top: 0, left: 0, right: 0 }}
          data={chartData.map(d => d.y)}
          contentInset={CHART_CONTENT_INSET}
          curve={shape.curveMonotoneX}
          svg={{
            stroke: chartColor,
            strokeWidth: 2,
          }}
          gridMin={yDomain.min}
          gridMax={yDomain.max}
          yMin={yDomain.min}
          yMax={yDomain.max}
        />

        {selectedPoint && (() => {
          const pointIndex = chartData.findIndex(d => d.x === selectedPoint.x);

          const xPosition = (pointIndex / Math.max(chartData.length - 1, 1)) * screenWidth;
          // Project through the same domain AND plot geometry the LineChart
          // renders with, so the dot lands on the line at every value.
          const yPosition = projectValueToPlotY(selectedPoint.y, yDomain, {
            height: CHART_HEIGHT,
            insetTop: CHART_CONTENT_INSET.top,
            insetBottom: CHART_CONTENT_INSET.bottom,
          });

          return (
            <Svg
              style={{ height: CHART_HEIGHT, width: screenWidth, position: 'absolute', top: 0, left: 0 }}
              pointerEvents="none"
            >
              <Circle
                cx={xPosition}
                cy={yPosition}
                r={5}
                stroke={chartColor}
                strokeWidth={2}
                fill={theme.colors.backgroundPrimary}
              />
            </Svg>
          );
        })()}

        {selectedPoint && (() => {
          const pointIndex = chartData.findIndex(d => d.x === selectedPoint.x);
          const xPosition = (pointIndex / Math.max(chartData.length - 1, 1)) * screenWidth;
          const tooltipWidth = 120;
          const isLeftSide = xPosition > screenWidth / 2;

          return (
            <View style={[
              styles.tooltip,
              {
                left: isLeftSide ? xPosition - tooltipWidth - 15 : xPosition + 15,
                top: 30,
              },
            ]}>
              <Text style={styles.tooltipDate}>
                {selectedPoint.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </Text>
              <Text style={styles.tooltipValue}>
                ${selectedPoint.y.toFixed(0)}
              </Text>
            </View>
          );
        })()}
      </View>

      <View style={styles.periodSelector}>
        {(['1W', '1M', '3M', 'YTD', 'All'] as TimePeriod[]).map((period) => (
          <TouchableOpacity
            key={period}
            style={[
              styles.periodButton,
              selectedPeriod === period && styles.periodButtonActive,
            ]}
            onPress={() => {
              setSelectedPeriod(period);
              setSelectedPoint(null);
            }}
          >
            <Text style={[
              styles.periodButtonText,
              selectedPeriod === period && styles.periodButtonTextActive,
            ]}>
              {period}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

